import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Banner } from '../ui/banner';
import { Mascot } from '../ui/mascot';
import { publicMaintenanceQuery } from '../../lib/workspace-api';

const WHEN = new Intl.DateTimeFormat('en-IN', {
  weekday: 'short',
  day: 'numeric',
  month: 'short',
  hour: 'numeric',
  minute: '2-digit',
  hour12: true,
});
const CLOCK = new Intl.DateTimeFormat('en-IN', { hour: 'numeric', minute: '2-digit', hour12: true });

/** The calm "back soon" page clients see while an admin has maintenance mode on (Figma A16.11 "What clients see"). */
export function MaintenancePage({ message, endsAt }: { message: string; endsAt?: string }) {
  return (
    <main className="flex min-h-dvh flex-col items-center justify-center gap-(--mbm-space-6) bg-surface-canvas px-(--mbm-space-4) text-center">
      <Mascot pose="think" className="h-(--mbm-size-mascot-height) w-(--mbm-size-mascot-width)" />
      <div className="flex max-w-(--mbm-layout-dialog-width) flex-col gap-(--mbm-space-2)">
        <h1 className="text-heading-h1 text-text-strong">Back soon</h1>
        <p className="text-body-medium text-text-secondary">
          {message}
          {endsAt ? ` We’ll be back by ${CLOCK.format(new Date(endsAt)).toUpperCase()}.` : ''}
        </p>
      </div>
    </main>
  );
}

/**
 * Wraps the client app: shows the "back soon" page while maintenance is on, and a heads-up banner in the 24 hours
 * before a scheduled window. If the status cannot be read the app is shown as normal (the API enforces it anyway).
 */
export function MaintenanceGate({ children }: { children: ReactNode }) {
  const { data } = useQuery({ ...publicMaintenanceQuery, retry: false, refetchInterval: 60_000 });
  if (data?.active) return <MaintenancePage message={data.message} endsAt={data.endsAt} />;
  return (
    <>
      {data?.upcoming ? (
        <Banner tone="warning" title="Planned maintenance" className="rounded-none">
          MyBusinessMate will be unavailable from {WHEN.format(new Date(data.upcoming.startsAt)).replace(',', '')} to{' '}
          {CLOCK.format(new Date(data.upcoming.endsAt)).toUpperCase()}. Your work and data are safe.
        </Banner>
      ) : null}
      {children}
    </>
  );
}
